import { ArrowLeftOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import { message, Typography } from "antd";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ProjectContext } from "context/ProjectProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import { useHistory, useParams } from "react-router";
import dayjs from "utils/dayjs-config";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { downloadFileInBrowser } from "utils/utils";
import {
  CustomReportPrintViewState,
  PRINT_VIEW_STORAGE_KEY
} from "pages/print-view-layout/model";
import { useCustomReports } from "../context/custom-reports-provider";
import { ContextReportDetail } from "../models";
import { exportCustomReportAsCSV } from "../services/custom-report-services";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { COMMENTS_EXPORT_COLUMN_ID } from "utils/comments-export";
import { attachCommentsToRows } from "pages/material-log-feature/material-comments";
import { fetchCommentsByReportRowId } from "../custom-report-comments";

const { Paragraph } = Typography;
const reportDetailsPlaceholder = "Enter Report Description";

/** Hidden in the grid; included in the Excel and PDF/Print exports. */
const EXPORT_ONLY_COLUMN_IDS = [COMMENTS_EXPORT_COLUMN_ID];

enum ExportCSVFormat {
  FORMATTED = "formatted", // flat_layout: false
  WITHOUT_FORMATTING = "without_formatting" // flat_layout: true
}

interface CustomReportsPageHeaderProps {
  disable?: boolean;
  onUpdateProjectReport: (
    data: any,
    showSuccessMessage?: boolean
  ) => Promise<void>;
  showExportOptions?: boolean;
  gridRef?: React.RefObject<AgGridReact<any>>;
}
function ReportsPageHeader({
  disable = false,
  onUpdateProjectReport,
  showExportOptions = false,
  gridRef
}: CustomReportsPageHeaderProps) {
  const history = useHistory();
  const { projectId } = useParams() as any;

  const {
    gridStatusBudge,
    detail,
    datasetConfig,
    columnConfig,
    conditionalsFormatting
  } = useCustomReports();

  const {
    projectToken,
    projectDetails,
    isFeatureFlagEnabled,
    gqlClientForProject,
    isProjectArchived
  } = useContext(ProjectContext);

  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");

  // Exports carry comments exactly when the Comments column is visible.
  const isCommentsColumnVisible = useMemo(
    () =>
      Array.isArray(columnConfig) &&
      columnConfig.some(
        (col: any) => col?.field_key === "comments" && !!col.visibility
      ),
    [columnConfig]
  );

  const [reportDetail, setReportDetail] = useState<ContextReportDetail | null>(
    detail
  );

  const [isDownloadingExportedCSV, setIsDownloadingExportedCSV] =
    useState(false);
  const [isFetchingComments, setIsFetchingComments] = useState(false);

  useEffect(() => {
    setReportDetail(detail);
  }, [detail]);

  const backButton = () => {
    history.push(`/project/${projectId}/reports/project-reports`);
  };

  const onExportCSV = useCallback(
    async (key?: any, includeComments = false) => {
      if (isDownloadingExportedCSV) {
        return;
      }
      setIsDownloadingExportedCSV(true);

      const payload = {
        flat_layout: key === ExportCSVFormat.WITHOUT_FORMATTING,
        include_comments: includeComments
      };
      const response = await exportCustomReportAsCSV(
        reportDetail?.id ?? "",
        projectToken,
        payload
      );

      if (!response.success) {
        setIsDownloadingExportedCSV(false);
        message.error(
          response.errorMsg || "An error occurred while exporting the report."
        );
        return;
      }

      if (response.data?.url) {
        downloadFileInBrowser(
          response.data.url,
          `${projectDetails?.name}_${reportDetail?.name}_${dayjs().format(
            DATE_FORMAT_MMDDYYYY
          )}.csv`,
          () => {
            setIsDownloadingExportedCSV(false);
          }
        );
      } else {
        setIsDownloadingExportedCSV(false);
      }
    },
    [
      isDownloadingExportedCSV,
      projectDetails,
      reportDetail?.id,
      reportDetail?.name,
      projectToken
    ]
  );

  const onOpenPrintLayoutMode = useCallback(
    (includeComments = false) => {
      if (!gridRef?.current || !reportDetail) {
        message.error(
          "Unable to open print layout mode. Grid data is not available."
        );
        return;
      }

      try {
        sessionStorage.clear();
        // Prepare print view state
        const printViewState: CustomReportPrintViewState = {
          pageId: "custom-material-report",
          reportId: reportDetail.id,
          datasetConfig,
          columnConfig,
          title: `${projectDetails?.name || "Project"} - ${
            reportDetail.name || "Custom Report"
          } - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
            `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
          )}`,
          initialState: gridRef.current.api.getState(),
          context: gridRef?.current?.props?.context || {},
          headerHeight: gridRef.current.props.headerHeight,
          // Comments wrap via autoHeight; a fixed height clips multi-line cells.
          rowHeight: includeComments
            ? undefined
            : gridRef.current.props.rowHeight,
          enableCellSpan: gridRef?.current?.props.enableCellSpan,
          skipColumnIds: ["comments"],
          includeComments,
          forceVisibleColumnIds: includeComments ? EXPORT_ONLY_COLUMN_IDS : [],
          conditionalsFormatting: conditionalsFormatting ?? []
        };

        // Store the state in sessionStorage
        sessionStorage.setItem(
          PRINT_VIEW_STORAGE_KEY,
          JSON.stringify(printViewState)
        );

        // Open the print view URL in a new tab
        const printViewUrl = `/project/${projectId}/printView`;
        window.open(printViewUrl, "_blank");
      } catch (error) {
        console.error("Error opening print layout mode:", error);
        message.error("An error occurred while opening print layout mode.");
      }
    },
    [
      datasetConfig,
      columnConfig,
      conditionalsFormatting,
      projectDetails?.name,
      reportDetail,
      projectId,
      gridRef
    ]
  );

  /** Returns null (after showing an error) when comments can't be fetched. */
  const loadCommentsForExport = useCallback(async () => {
    setIsFetchingComments(true);
    try {
      const commentsById = await fetchCommentsByReportRowId(
        reportDetail?.id ?? "",
        projectToken
      );
      if (!commentsById)
        message.error("Unable to fetch comments. Please try again.");
      return commentsById;
    } finally {
      setIsFetchingComments(false);
    }
  }, [reportDetail?.id, projectToken]);

  const onExportExcel = useCallback(
    (
      options: ExcelExportOptions,
      commentsById: Map<string, string[]> | null = null
    ) => {
      if (!gridRef?.current || !projectDetails) return;
      const filename: string = `Custom Report_${sanitizeSheetName(projectDetails.name)}.xlsx`;
      if (commentsById) attachCommentsToRows(gridRef.current.api, commentsById);
      exportEntityLogAsExcel({
        filename,
        projectDetails,
        gridRef,
        includedColumns: commentsById ? EXPORT_ONLY_COLUMN_IDS : [],
        excludedColumns: ["comments"],
        groupByField: datasetConfig?.groupByField,
        options,
        prependText: `${projectDetails.name} - ${
          reportDetail?.name || "Custom Report"
        } - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`
      });

      saveAuditLogEvent({
        eventType: "CUSTOM_MATERIAL_REPORT_EXPORT_EXCEL",
        featureType: "MATERIALS",
        filename
      });
    },
    [
      gridRef,
      projectDetails,
      datasetConfig?.groupByField,
      reportDetail?.name,
      saveAuditLogEvent
    ]
  );

  const exportOptions: ExportFormatOption[] = useMemo(() => {
    const options: ExportFormatOption[] = [
      {
        key: ExportCSVFormat.FORMATTED,
        title: "CSV (Formatted)",
        description: "Cleaner format as seen in-app",
        testId: "custom-report-export-csv-formatted"
      }
    ];
    if (showExportOptions) {
      options.push({
        key: ExportCSVFormat.WITHOUT_FORMATTING,
        title: "CSV (Raw Data)",
        description: "Repeated data to avoid blank cells",
        testId: "custom-report-export-csv-raw"
      });
    }
    if (isExcelExportEnabled && gridRef) {
      options.push({
        key: "excel",
        title: "Excel (.xlsx)",
        description: "Download the report as an Excel file",
        isExcel: true,
        testId: "custom-report-export-excel"
      });
    }
    if (isPrintViewEnabled) {
      options.push({
        key: "print-layout",
        title: "PDF/Print",
        description: "Open a print-friendly view",
        testId: "custom-report-export-print"
      });
    }
    return options;
  }, [gridRef, isExcelExportEnabled, isPrintViewEnabled, showExportOptions]);

  const onProceedExport = useCallback(
    async ({ key, excelOptions }: ExportSelection) => {
      if (key === "excel") {
        if (!excelOptions) return;
        let commentsById: Map<string, string[]> | null = null;
        if (isCommentsColumnVisible) {
          commentsById = await loadCommentsForExport();
          if (!commentsById) return;
        }
        onExportExcel(excelOptions, commentsById);
        return;
      }
      if (key === "print-layout") {
        if (gridRef?.current)
          PrintViewValidationModal({
            gridApi: gridRef.current.api,
            onProceed: () => onOpenPrintLayoutMode(isCommentsColumnVisible),
            skipColumnIds: ["comments", "ag-Grid-SelectionColumn"],
            forceVisibleColumnIds: isCommentsColumnVisible
              ? EXPORT_ONLY_COLUMN_IDS
              : []
          });
        return;
      }
      onExportCSV(key, isCommentsColumnVisible);
    },
    [
      gridRef,
      isCommentsColumnVisible,
      loadCommentsForExport,
      onExportCSV,
      onExportExcel,
      onOpenPrintLayoutMode
    ]
  );

  return (
    <div
      className="w-full px-6 py-4 flex justify-between gap-x-6 -mb-4"
      data-testid="custom-report-header-container"
    >
      {/* Left Section */}
      <div className="grow flex space-x-4">
        {/* Back Arrow */}
        <div className="flex items-center">
          <ArrowLeftOutlined
            className="text-black text-base cursor-pointer mb-2"
            onClick={backButton}
            data-testid="custom-report-back-button"
          />
        </div>

        {/* Title + Description */}
        {reportDetail && (
          <div className="grow flex flex-col">
            <div
              className="flex space-x-4 items-center"
              data-testid="custom-report-title-section"
            >
              <Typography.Title
                style={{ margin: 0 }}
                level={5}
                editable={
                  disable
                    ? false
                    : {
                        onChange: (newValue) => {
                          setReportDetail((pre) =>
                            pre
                              ? {
                                  ...pre,
                                  name: newValue
                                }
                              : null
                          );
                          onUpdateProjectReport({ name: newValue }, true);
                        },
                        triggerType: ["text", "icon"]
                      }
                }
                data-testid="custom-report-title"
              >
                {reportDetail?.name}
              </Typography.Title>
              {gridStatusBudge}
            </div>
            <div className="w-1/2 pt-1">
              <Paragraph
                type="secondary"
                ellipsis
                editable={
                  disable
                    ? false
                    : {
                        onChange: (newValue) => {
                          if (newValue.trim() === reportDetailsPlaceholder) {
                            return;
                          }
                          setReportDetail((pre) =>
                            pre
                              ? {
                                  ...pre,
                                  description: newValue
                                }
                              : null
                          );
                          onUpdateProjectReport(
                            { description: newValue },
                            true
                          );
                        },
                        triggerType: ["text", "icon"]
                      }
                }
                className="m-0 text-sm"
                data-testid="custom-report-description"
              >
                {reportDetail?.description || reportDetailsPlaceholder}
              </Paragraph>
            </div>
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-start">
        <div className="flex items-center space-x-2 mt-2">
          {/* <Button icon={<MailOutlined />}>Email Alert</Button> */}

          <ExportOptionsPopover
            options={exportOptions}
            onProceed={onProceedExport}
            showIncludeComments={isCommentsColumnVisible}
            includeCommentsNote="Export includes comments"
            excelTableName={projectDetails?.name || "Project"}
            loading={isDownloadingExportedCSV || isFetchingComments}
            disabled={!!isProjectArchived}
            buttonTestId="custom-report-export-button"
          />

          {/* <Popover placement="left" content={<div>Help Content</div>}>
            <QuestionCircleOutlined className="pl-2 text-gray-600 hover:text-gray-800 cursor-pointer" />
          </Popover> */}
        </div>
      </div>
    </div>
  );
}

export default ReportsPageHeader;
